import { useEffect, useId, useRef, useState } from 'react';
import { useOpenHoverCard } from '@/stores/open-hover-card.store';

export const HOVER_INTENT_OPEN_DELAY_MS = 1000;
export const HOVER_INTENT_CLOSE_DELAY_MS = 150;

interface UseHoverIntentOptions {
    openDelayMs?: number;
    closeDelayMs?: number;
}

interface UseHoverIntentResult {
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
    handlePointerEnter: () => void;
    handlePointerLeave: () => void;
}

export function useHoverIntent({
    openDelayMs = HOVER_INTENT_OPEN_DELAY_MS,
    closeDelayMs = HOVER_INTENT_CLOSE_DELAY_MS,
}: UseHoverIntentOptions = {}): UseHoverIntentResult {
    const cardId = useId();
    const openCardId = useOpenHoverCard((state) => state.openCardId);
    const openCard = useOpenHoverCard((state) => state.open);
    const closeCard = useOpenHoverCard((state) => state.close);
    const pendingChange = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Reactive so the scroll listener below can be installed while an open is
    // still pending, not just while a card is already open.
    const [isPending, setIsPending] = useState(false);

    const cancelPendingChange = () => {
        if (pendingChange.current !== null) {
            clearTimeout(pendingChange.current);
            pendingChange.current = null;
        }
        setIsPending(false);
    };

    useEffect(() => cancelPendingChange, []);
    useEffect(() => () => closeCard(cardId), [cardId, closeCard]);

    // Scrolling must close an open card and also cancel one still waiting on the
    // open delay. Gating on those two states leaves at most a couple of document
    // listeners however many cards mount.
    useEffect(() => {
        if (openCardId !== cardId && !isPending) return;
        const closeOnScroll = () => {
            cancelPendingChange();
            closeCard(cardId);
        };
        const listenerOptions = { capture: true, passive: true };
        document.addEventListener('scroll', closeOnScroll, listenerOptions);
        return () =>
            document.removeEventListener(
                'scroll',
                closeOnScroll,
                listenerOptions,
            );
    }, [cardId, openCardId, isPending, closeCard]);

    const schedule = (change: () => void, delayMs: number) => {
        cancelPendingChange();
        if (delayMs === 0) {
            change();
            return;
        }
        setIsPending(true);
        pendingChange.current = setTimeout(() => {
            setIsPending(false);
            change();
        }, delayMs);
    };

    const setIsOpen = (open: boolean) => {
        cancelPendingChange();
        if (open) {
            openCard(cardId);
            return;
        }
        closeCard(cardId);
    };

    return {
        isOpen: openCardId === cardId,
        setIsOpen,
        handlePointerEnter: () => schedule(() => openCard(cardId), openDelayMs),
        handlePointerLeave: () =>
            schedule(() => closeCard(cardId), closeDelayMs),
    };
}
